import Select from '~/components/Select';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import Toggle from '~/components/Toggle';
import MoneyInput from '~/components/MoneyInput';
import React, { useCallback, useMemo, useState } from 'react';
import TextInput from '~/components/TextInput';
import TextInputWithInlineLabel from '~/components/TextInputWithInlineLabel';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ObjectTypes } from '~/lib/ObjectTypes';
import { useEventTypesQuery } from '~/lib/Queries';
import * as Toast from '~/lib/Toast';
import GateRequiresPaymentProcessor from '~/components/GateRequiresPaymentProcessor';
import { renewalPeriodOptions } from '~/lib/PackageHelpers';

const DiscountRow = ({ options, discount, onChange, onDelete, eventType }) => {
    return (
        <div className="flex items-center space-x-2 pr-6 mb-2">
            <div className="flex-1">
                <Select
                    options={options}
                    value={discount.id}
                    onChange={value =>
                        onChange({
                            ...discount,
                            id: value,
                            type: ObjectTypes.EVENT_TYPE,
                        })
                    }
                    inputClassName="max-w-64"
                />
            </div>
            <div className="w-24">
                {!!eventType && (
                    <>
                        {eventType.price > 0 && (
                            <>
                                {Math.round(
                                    (1 - discount.price / eventType.price) *
                                        100,
                                ) || 0}
                                %
                            </>
                        )}
                        {eventType.price <= 0 && (
                            <span className="text-lg">💥</span>
                        )}
                        {/*<TextInputWithInlineLabel*/}
                        {/*    value={*/}
                        {/*        Math.round(*/}
                        {/*            (discount.price / eventType.price) * 100,*/}
                        {/*        ) || 0*/}
                        {/*    }*/}
                        {/*    suffix="%"*/}
                        {/*    disabled={true}*/}
                        {/*/>*/}
                    </>
                )}
            </div>
            <div className="w-24">
                <MoneyInput
                    value={discount.price}
                    onChange={value => onChange({ ...discount, price: value })}
                />
            </div>
            <button
                type="button"
                className="btn-pink-circle !w-6 !h-6"
                onClick={() => {
                    if (
                        confirm(
                            'Are you sure you want to remove this discount?',
                        )
                    ) {
                        onDelete();
                    }
                }}
            >
                <XMarkIcon className="w-5 h-5" />
            </button>
        </div>
    );
};

const InputSubscriptionAdditionalSettings = ({
    data,
    setData,
    setSubscriptionSettings,
}) => {
    const { data: eventTypeData } = useEventTypesQuery();
    const [showSetupFee, setShowSetupFee] = useState(false);
    const hasSetupFee =
        showSetupFee || parseFloat(data?.subscription_settings?.setupFee) !== 0;

    const [showDiscount, setShowDiscount] = useState(false);
    const hasDiscount =
        showDiscount || data?.subscription_settings?.discount?.length > 0;
    const discounts = data?.subscription_settings?.discount || [];

    const discountOptions = [{ id: 0, label: '' }];
    if (eventTypeData?.length > 0) {
        eventTypeData.map(et => {
            discountOptions.push({
                id: et.id,
                label: `${et.name} (${formatCurrency(et.price)})`,
                requires_package: et.requires_package,
            });
        });
    }
    const canAddDiscount = discountOptions.length - 1 > discounts.length;

    const getThisDiscountOptions = useCallback(
        (discountOptions, discount, allDiscounts) => {
            let opts = [];
            discountOptions.forEach(opt => {
                if (
                    parseInt(opt.id) === parseInt(discount.id) ||
                    !allDiscounts.find(d => parseInt(d.id) === parseInt(opt.id))
                ) {
                    opts.push({
                        value: opt.id,
                        label:
                            opt.label +
                            (opt.requires_package
                                ? ' (requires package, cannot discount)'
                                : ''),
                        disabled: opt.requires_package,
                    });
                }
            });
            return opts;
        },
        [],
    );

    function addNewDiscountRow() {
        setSubscriptionSettings({
            ...(data?.subscription_settings || {}),
            discount: [...(data?.subscription_settings?.discount || []), {}],
        });
    }

    const maxCyclesOptions = useMemo(() => {
        let opts = {};
        opts = { 0: 'Forever, until canceled' };
        for (let i = 2; i <= 24; i++) {
            opts[i] = `${i} times`;
        }

        return opts;
    }, []);

    return (
        <>
            <div className="panel-label mt-6">Pricing & Billing</div>
            <div className="panel p-6">
                <div className="flex items-center space-x-4">
                    <MoneyInput
                        value={data?.price || ''}
                        onChange={value => setData({ ...data, price: value })}
                        placeholder="Free"
                        label="Price"
                    />

                    <Select
                        label="Renews"
                        options={renewalPeriodOptions}
                        value={data?.subscription_settings?.renewalPeriod}
                        onChange={newValue =>
                            setSubscriptionSettings(old => ({
                                ...old,
                                renewalPeriod: newValue,
                            }))
                        }
                    />
                </div>
                <GateRequiresPaymentProcessor className="mt-2" />

                <Select
                    label="Max Charges"
                    options={maxCyclesOptions}
                    value={data?.subscription_settings?.maxCycles}
                    onChange={newValue =>
                        setSubscriptionSettings(old => ({
                            ...old,
                            maxCycles: newValue,
                        }))
                    }
                    className="mt-4"
                />

                <div className="text-sm mt-4 text-gray-500 ml-2">
                    💰 Clients will be charged{' '}
                    {formatCurrency(data?.price || 0)}{' '}
                    {renewalPeriodOptions[
                        data?.subscription_settings?.renewalPeriod
                    ]?.toLowerCase()}{' '}
                    {data?.subscription_settings?.maxCycles === '0' && (
                        <span>forever until this is canceled</span>
                    )}{' '}
                    {data?.subscription_settings?.maxCycles > 0 && (
                        <span className="font-semibold">
                            for a max of{' '}
                            {data?.subscription_settings?.maxCycles} payments (
                            {formatCurrency(
                                data?.subscription_settings?.maxCycles *
                                    data?.price || 0,
                            )}{' '}
                            total)
                        </span>
                    )}
                </div>
            </div>

            <div className="panel-label mt-6">Additional Options</div>
            <div className="panel p-6 pr-0">
                <div className="flex items-center justify-between pr-6">
                    Charge Initial Setup Fee
                    <Toggle
                        enabled={hasSetupFee}
                        setEnabled={isEnabled => {
                            setShowSetupFee(isEnabled);
                            setSubscriptionSettings(old => ({
                                ...old,
                                setupFee: isEnabled ? 10 : 0,
                            }));
                        }}
                    />
                </div>
                {hasSetupFee && (
                    <MoneyInput
                        className="mt-4 max-w-32"
                        autoFocus={true}
                        value={data?.subscription_settings?.setupFee || ''}
                        onChange={newValue =>
                            setSubscriptionSettings(old => ({
                                ...old,
                                setupFee: newValue,
                            }))
                        }
                    />
                )}
                <div className="flex items-center justify-between pt-4 mt-4 pr-6 border-t">
                    Discount other services for subscribers
                    <Toggle
                        enabled={hasDiscount}
                        setEnabled={isEnabled => {
                            setShowDiscount(isEnabled);
                            if (isEnabled && discounts.length === 0)
                                addNewDiscountRow();
                            if (
                                !isEnabled &&
                                discounts.length === 1 &&
                                !parseInt(discounts[0]?.id)
                            ) {
                                setSubscriptionSettings({
                                    ...data?.subscription_settings,
                                    discount: [],
                                });
                            } else if (!isEnabled) {
                                Toast.error(
                                    'Please remove all discounts to disable',
                                );
                            }
                        }}
                    />
                </div>
                {hasDiscount && (
                    <div className="mt-8">
                        <div className="pr-6">
                            <div className="mb-2 text-xs text-gray-500 pr-6 uppercase font-semibold flex space-x-2">
                                <div className="flex-1 pl-2">Event Type</div>
                                <div className="w-24 pl-1">Discount</div>
                                <div className="w-24 pl-1">Price</div>
                                <div className="w-0">&nbsp;</div>
                            </div>
                        </div>
                        {discounts.map((discount, idx) => (
                            <DiscountRow
                                key={idx}
                                discount={discount}
                                options={getThisDiscountOptions(
                                    discountOptions,
                                    discount,
                                    discounts,
                                )}
                                eventType={(eventTypeData || []).find(
                                    et =>
                                        parseInt(et.id) ===
                                        parseInt(discount.id),
                                )}
                                onChange={newDiscount => {
                                    let newDiscounts = [
                                        ...data?.subscription_settings
                                            ?.discount,
                                    ];
                                    newDiscounts[idx] = newDiscount;
                                    setSubscriptionSettings({
                                        ...data?.subscription_settings,
                                        discount: newDiscounts,
                                    });
                                }}
                                onDelete={() => {
                                    let newDiscounts = [
                                        ...data?.subscription_settings
                                            ?.discount,
                                    ];
                                    newDiscounts.splice(idx, 1);
                                    setSubscriptionSettings({
                                        ...data?.subscription_settings,
                                        discount: newDiscounts,
                                    });
                                }}
                            />
                        ))}

                        {canAddDiscount && (
                            <button
                                type="button"
                                className="btn-light-pill mt-2"
                                onClick={() => {
                                    addNewDiscountRow();
                                }}
                            >
                                Add Discount
                            </button>
                        )}
                    </div>
                )}
                <div className="flex items-center justify-between pt-4 mt-4 pr-6 border-t ">
                    Allow Clients to Cancel
                    <Toggle
                        enabled={
                            !data?.subscription_settings?.disableCancellation
                        }
                        setEnabled={isEnabled => {
                            setSubscriptionSettings(old => ({
                                ...old,
                                disableCancellation: !isEnabled,
                            }));
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default InputSubscriptionAdditionalSettings;
